import React, { useState } from 'react';
import styled from 'styled-components';
import { FaBars, FaTimes } from 'react-icons/fa';
import { navigationItems, socialLinks } from '../data/navigation';
import { personalInfo } from '../data/personal';
import { useScrollSection } from '../hooks';

const HeaderContainer = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all ${({ theme }) => theme.transitions.normal};
  background: ${({ $scrolled, theme }) =>
    $scrolled
      ? `rgba(10, 10, 10, 0.95)`
      : 'transparent'};
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(10px)' : 'none'};
  border-bottom: ${({ $scrolled, theme }) =>
    $scrolled ? `1px solid ${theme.colors.border}` : '1px solid transparent'};

  /* Improve mobile performance */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(8px)' : 'none'}; /* Reduced blur for better mobile performance */
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 ${({ theme }) => theme.spacing.lg}; /* Increased mobile padding */
    height: 70px; /* Slightly shorter on mobile to save space */
  }
`;

const Logo = styled.a`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.textPrimary}, ${({ theme }) => theme.colors.navyBlue});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    transform: scale(1.05);
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing['2xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  gap: ${({ theme }) => theme.spacing.xl};
  margin: 0;
  padding: 0;
`;

const NavItem = styled.li``;

const NavLink = styled.a<{ $active: boolean }>`
  color: ${({ $active, theme }) =>
    $active ? theme.colors.navyBlue : theme.colors.textSecondary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.fontSizes.base};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;

  &:hover {
    color: ${({ theme }) => theme.colors.navyBlue};
    transform: translateY(-1px);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: ${({ $active }) => $active ? '100%' : '0'};
    height: 2px;
    background: ${({ theme }) => theme.colors.navyBlue};
    transition: width ${({ theme }) => theme.transitions.normal};
  }

  &:hover::after {
    width: 100%;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SocialLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    color: ${({ theme }) => theme.colors.navyBlue};
    transform: translateY(-2px);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  
  /* Better touch target */
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-width: 44px; /* Minimum touch target size */
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors.navyBlue};
  }
  
  /* Touch feedback for mobile */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      background: rgba(30, 58, 138, 0.1);
      transform: scale(0.95);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex; /* Changed from block to flex for better centering */
  }
`;

const MobileMenu = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 70px; /* Match mobile header height */
  left: 0;
  right: 0;
  background: rgba(10, 10, 10, 0.98);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  transform: ${({ $open }) => $open ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${({ $open }) => $open ? 1 : 0};
  visibility: ${({ $open }) => $open ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.transitions.normal};
  z-index: 999;
  
  /* Improve mobile performance */
  will-change: transform, opacity;
  
  /* Add safe area padding for notched devices */
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    top: 70px; /* Match mobile header height */
  }
`;

const MobileMenuContent = styled.div`
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  
  /* Add safe area padding */
  padding-left: calc(${({ theme }) => theme.spacing.lg} + env(safe-area-inset-left));
  padding-right: calc(${({ theme }) => theme.spacing.lg} + env(safe-area-inset-right));
`;

const MobileNavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const MobileNavItem = styled.li``;

const MobileNavLink = styled.a<{ $active: boolean }>`
  display: block;
  padding: ${({ theme }) => theme.spacing.lg}; /* Increased padding for better touch targets */
  color: ${({ $active, theme }) =>
    $active ? theme.colors.navyBlue : theme.colors.textPrimary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg}; /* Increased border radius */
  transition: all ${({ theme }) => theme.transitions.normal};
  background: ${({ $active, theme }) =>
    $active ? 'rgba(30, 58, 138, 0.1)' : 'transparent'};
  
  /* Minimum touch target size */
  min-height: 48px;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(30, 58, 138, 0.1);
    color: ${({ theme }) => theme.colors.navyBlue};
  }
  
  /* Touch feedback for mobile */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      background: rgba(30, 58, 138, 0.2);
      transform: scale(0.98);
    }
  }
`;

const MobileSocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xl}; /* Increased gap for better touch targets */
  padding-top: ${({ theme }) => theme.spacing.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const MobileSocialLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  transition: all ${({ theme }) => theme.transitions.normal};
  
  /* Better touch targets */
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  min-width: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors.navyBlue};
    transform: scale(1.1);
  }
  
  /* Touch feedback for mobile */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      background: rgba(30, 58, 138, 0.1);
      transform: scale(0.95);
    }
  }
`;

export const HeaderSection: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { activeSection, scrolled } = useScrollSection();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleNavClick = (href: string) => {
    const targetId = href.replace('#', '');
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
    closeMobileMenu();
  };

  return (
    <>
      <HeaderContainer $scrolled={scrolled} role="banner">
        <HeaderContent>
          <Logo 
            href="#home" 
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#home');
            }}
            aria-label="Jeremiah Manuel - Go to homepage"
          >
            {personalInfo.name}
          </Logo>

          <Nav role="navigation" aria-label="Main navigation">
            <NavList>
              {navigationItems.map((item) => (
                <NavItem key={item.id}>
                  <NavLink
                    href={item.href}
                    $active={activeSection === item.id}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(item.href);
                    }}
                    aria-current={activeSection === item.id ? 'page' : undefined}
                    aria-label={`Navigate to ${item.label} section`}
                  >
                    {item.label}
                  </NavLink>
                </NavItem>
              ))}
            </NavList>

            <SocialLinks role="list" aria-label="Social media links">
              {socialLinks.map((link) => (
                <SocialLink
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit Jeremiah's ${link.name} profile (opens in new tab)`}
                  role="listitem"
                >
                  {React.createElement(link.icon as any)}
                </SocialLink>
              ))}
            </SocialLinks>
          </Nav>

          <MobileMenuButton 
            onClick={toggleMobileMenu}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {mobileMenuOpen ? React.createElement(FaTimes as any) : React.createElement(FaBars as any)}
          </MobileMenuButton>
        </HeaderContent>
      </HeaderContainer>

      <MobileMenu
        $open={mobileMenuOpen}
        id="mobile-menu"
        role="navigation"
        aria-label="Mobile navigation menu"
        aria-hidden={!mobileMenuOpen}
      >
        <MobileMenuContent>
          <MobileNavList role="list">
            {navigationItems.map((item) => (
              <MobileNavItem key={item.id} role="listitem">
                <MobileNavLink
                  href={item.href}
                  $active={activeSection === item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }}
                  aria-current={activeSection === item.id ? 'page' : undefined}
                  aria-label={`Navigate to ${item.label} section`}
                >
                  {item.label}
                </MobileNavLink>
              </MobileNavItem>
            ))}
          </MobileNavList>

          <MobileSocialLinks role="list" aria-label="Social media links">
            {socialLinks.map((link) => (
              <MobileSocialLink
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit Jeremiah's ${link.name} profile (opens in new tab)`}
                role="listitem"
              >
                {React.createElement(link.icon as any)}
              </MobileSocialLink>
            ))}
          </MobileSocialLinks>
        </MobileMenuContent>
      </MobileMenu>
    </>
  );
}; 